import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { PartnerInfo, UserStateModel } from './model/user-state.model';
import { ImmutableContext, ImmutableSelector } from '@ngxs-labs/immer-adapter';
import { LogoutAction } from './action/logout.action';
import { HttpClient } from '@angular/common/http';
import { LoginAction } from './action/login.action';
import { TenapodExternalAuthAction } from './action/tenapod-external-auth.action';
import { deepCopy } from '@partneradmin/common';
import { environment } from '@partneradmin/env';
import { mergeMap, tap } from 'rxjs';
import { ChangePasswordAction } from './action/change-password.action';
import { LoginWithTokenAction } from './action/login-with-token.action';
import { ReloadPartnerInfoAction } from './action/reload-partner-info.action';
import { UserEntity } from './entity/user.entity';
import { isBoolean, isNil } from '@partneradmin/typeguard';

export const USER_STATE_KEY = 'user';

const defaultState: UserStateModel = { token: null, user: null, partner: null };

@State<UserStateModel>({
  name: USER_STATE_KEY,
  defaults: {
    ...defaultState,
  },
})
@Injectable()
export class UserState implements NgxsOnInit {
  constructor(private http: HttpClient) {}

  @Selector()
  @ImmutableSelector()
  static isLoggedIn(state: UserStateModel): boolean {
    return state.token !== null;
  }

  @Selector()
  @ImmutableSelector()
  static currentUser(state: UserStateModel) {
    return state.user;
  }

  @Selector()
  @ImmutableSelector()
  static partner(state: UserStateModel): PartnerInfo | null {
    return state.partner;
  }

  /**
   * A bejelentkezett partner AES-titkosított email címe, vagy `null`,
   * ha még nincs bejelentkezett user vagy a backend nem küldte el.
   *
   * @param state Aktuális user state.
   * @returns A titkosított email vagy `null`.
   */
  @Selector()
  @ImmutableSelector()
  static encryptedEmail(state: UserStateModel): string | null {
    return state.partner?.encryptedEmail ?? null;
  }

  @Selector()
  @ImmutableSelector()
  static token(state: UserStateModel): string | null {
    return state.token;
  }

  /**
   * Amíg nincs betöltve a partner adat, a Tenapod menüpontok láthatók
   * maradnak — az elrejtés csak akkor történik, ha a backend explicit
   * `false`-t küldött az `announcements_tenapod` mezőben.
   */
  @Selector()
  @ImmutableSelector()
  static announcementsTenapod(state: UserStateModel): boolean {
    return state.partner?.announcementsTenapod ?? true;
  }

  /**
   * A Pillanatok menüpont opt-in: csak akkor látható, ha a backend
   * explicit `true`-t küldött az `announcements_pillanatok` mezőben.
   */
  @Selector()
  @ImmutableSelector()
  static announcementsPillanatok(state: UserStateModel): boolean {
    return state.partner?.announcementsPillanatok ?? false;
  }

  @Selector()
  @ImmutableSelector()
  static hasPassword(state: UserStateModel): boolean {
    return isBoolean(state.hasPassword) ? state.hasPassword : false;
  }

  @Selector()
  @ImmutableSelector()
  static hasPasswordEmail(state: UserStateModel): string | undefined {
    return state.hasPasswordEmail;
  }

  ngxsOnInit(ctx?: StateContext<any>): any {
    const state = ctx?.getState();
    if (!isNil(state) && UserState.isLoggedIn(state)) {
      ctx?.dispatch(new ReloadPartnerInfoAction());
    }
  }

  @Action(LoginAction)
  @ImmutableContext()
  login({ setState }: StateContext<UserStateModel>, { email, password }: LoginAction) {
    return this.http
      .post<{ token: string; user: any; partner: any /*TODO meg nem kaptunk rendes leirot hozza!*/ }>(
        `${environment.backendUrl}/loginpartner`,
        { email, password }
      )
      .pipe(
        tap(({ token, user, partner }) => {
          setState((state: UserStateModel) => {
            const loggedPartnerInfo: PartnerInfo = {
              id: partner.id,
              company_name: partner.companyName,
              encryptedEmail: user?.encryptedEmail ?? null,
            };

            state.user = user;
            state.token = token;
            state.partner = loggedPartnerInfo;

            return state;
          });
        }),
        mergeMap(() => this.http.get<UserEntity>(`${environment.backendUrl}/partner/auth`)),
        tap(({ has_password, announcements_tenapod, announcements_pillanatok }: UserEntity) => {
          setState((state: UserStateModel) => {
            state.hasPassword = has_password;
            if (state.partner) {
              state.partner.announcementsTenapod = announcements_tenapod;
              state.partner.announcementsPillanatok = announcements_pillanatok;
            }
            return state;
          });
        })
      );
  }

  /**
   * Sign in a partner forwarded from an external (Tenapod) site.
   *
   * Mirrors `LoginAction` but hits `/partner/tenapod/external-auth` with
   * `{ email, path }` instead of email/password. On success, populates
   * token/user/partner in state, then fetches `/partner/auth` to populate
   * `hasPassword`. Errors propagate to the dispatcher.
   *
   * @param ctx    NGXS state context.
   * @param action The dispatched action carrying `email` and `path`.
   * @returns The RxJS chain producing the final `UserEntity` response.
   */
  @Action(TenapodExternalAuthAction)
  @ImmutableContext()
  tenapodExternalAuth(
    { setState }: StateContext<UserStateModel>,
    { email, path, expirationDate }: TenapodExternalAuthAction
  ) {
    return this.http
      .post<{ token: string; user: any; partner: any }>(
        `${environment.backendUrl}/partner/tenapod/external-auth`,
        { email, path, expiration_date: expirationDate }
      )
      .pipe(
        tap(({ token, user, partner }) => {
          setState((state: UserStateModel) => {
            const loggedPartnerInfo: PartnerInfo = {
              id: partner.id,
              company_name: partner.companyName,
              encryptedEmail: user?.encryptedEmail ?? null,
            };

            state.user = user;
            state.token = token;
            state.partner = loggedPartnerInfo;

            return state;
          });
        }),
        mergeMap(() => this.http.get<UserEntity>(`${environment.backendUrl}/partner/auth`)),
        tap(({ has_password, announcements_tenapod, announcements_pillanatok }: UserEntity) => {
          setState((state: UserStateModel) => {
            state.hasPassword = has_password;
            if (state.partner) {
              state.partner.announcementsTenapod = announcements_tenapod;
              state.partner.announcementsPillanatok = announcements_pillanatok;
            }
            return state;
          });
        })
      );
  }

  @Action(ChangePasswordAction)
  @ImmutableContext()
  changePassword(
    { setState }: StateContext<UserStateModel>,
    { oldPassword, newPassword, newPassword2 }: ChangePasswordAction
  ) {
    const body: Record<string, string> = {
      password: newPassword,
      password_confirmation: newPassword2,
    };

    if (!isNil(oldPassword)) {
      body['old_password'] = oldPassword;
    }

    return this.http.patch<void>(`${environment.backendUrl}/partner/auth/password`, body).pipe(
      tap(() => {
        setState((state: UserStateModel) => {
          state.hasPassword = true;
          return state;
        });
      })
    );
  }

  @Action(LogoutAction)
  @ImmutableContext()
  logout({ setState }: StateContext<UserStateModel>) {
    setState((state: UserStateModel) => {
      return deepCopy(defaultState);
    });
  }

  @Action(LoginWithTokenAction)
  @ImmutableContext()
  loginWithToken({ setState }: StateContext<UserStateModel>, { token }: LoginWithTokenAction) {
    setState((state: UserStateModel) => {
      state.token = token;
      return state;
    });
  }

  @Action(ReloadPartnerInfoAction)
  @ImmutableContext()
  reloadUserData({ setState }: StateContext<UserStateModel>) {
    return this.http.get<UserEntity>(`${environment.backendUrl}/partner/auth`).pipe(
      tap((user: UserEntity) => {
        setState((state: UserStateModel) => {
          const loggedPartnerInfo: PartnerInfo = {
            id: user.id,
            company_name: user.company_name,
            encryptedEmail: state.partner?.encryptedEmail ?? null,
            announcementsTenapod: user.announcements_tenapod,
            announcementsPillanatok: user.announcements_pillanatok,
          };

          state.partner = loggedPartnerInfo;
          state.hasPassword = user.has_password;
          state.hasPasswordEmail = user.company_email;
          return state;
        });
      })
    );
  }
}
